import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ContributingEntityRole,
  useUpdateContributingEntityMutation,
  type ContributingEntityDto,
  type SelectionCriterionDto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { GroupRoleField, declaresGroupRole } from "./GroupRoleField";
import { SupportedCriteriaField } from "./SupportedCriteriaField";

interface FormValues {
  name: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  groupRole: string;
  supportedCriterionKeys: string[];
}

interface Props {
  bidId: string;
  entity: ContributingEntityDto;
  selectionCriteria: SelectionCriterionDto[];
  onClose: () => void;
}

export const EditContributingEntityModal = ({ bidId, entity, selectionCriteria, onClose }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [updateContributingEntity, { isLoading }] = useUpdateContributingEntityMutation();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: entity.name,
      contactFirstName: entity.contactFirstName ?? "",
      contactLastName: entity.contactLastName ?? "",
      contactEmail: entity.contactEmail,
      groupRole: entity.groupRole ?? "",
      supportedCriterionKeys: entity.supportedCriterionKeys ?? [],
    },
  });

  const lendsCapacity = entity.role === ContributingEntityRole.CapacityProvider;
  const needsGroupRole = declaresGroupRole(entity.role);
  const supportedCriterionKeys = watch("supportedCriterionKeys") ?? [];

  register("supportedCriterionKeys", {
    validate: (value) =>
      !lendsCapacity ||
      selectionCriteria.length === 0 ||
      value.length > 0 ||
      (t("Select at least one qualification requirement") ?? ""),
  });

  const submit = async (values: FormValues) => {
    try {
      await updateContributingEntity({
        bidId,
        entityId: entity.id,
        updateContributingEntityRequestDto: {
          role: entity.role,
          name: values.name.trim(),
          contactFirstName: values.contactFirstName.trim() || null,
          contactLastName: values.contactLastName.trim() || null,
          contactEmail: values.contactEmail.trim(),
          groupRole: needsGroupRole ? values.groupRole.trim() : null,
          supportedCriterionKeys: lendsCapacity ? values.supportedCriterionKeys : [],
        },
      }).unwrap();
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Edit participating company")}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <Stack spacing="4">
              <FormControl isRequired isInvalid={errors.name !== undefined}>
                <FormLabel fontSize="sm">{t("Company name")}</FormLabel>
                <Input {...register("name", { required: t("Company name is required") ?? "" })} />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="4">
                <FormControl>
                  <FormLabel fontSize="sm">{t("Contact first name")}</FormLabel>
                  <Input {...register("contactFirstName")} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t("Contact family name")}</FormLabel>
                  <Input {...register("contactLastName")} />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={errors.contactEmail !== undefined}>
                <FormLabel fontSize="sm">{t("Contact email")}</FormLabel>
                <Input
                  type="email"
                  {...register("contactEmail", {
                    required: t("Contact email is required") ?? "",
                    validate: (value) => value.includes("@") || (t("Enter a valid email address") ?? ""),
                  })}
                />
                <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
              </FormControl>

              {needsGroupRole && (
                <GroupRoleField
                  role={entity.role}
                  registration={register("groupRole", {
                    validate: (value) =>
                      !needsGroupRole || value.trim().length > 0 || (t("This field is required") ?? ""),
                  })}
                  error={errors.groupRole?.message}
                />
              )}

              {lendsCapacity && (
                <SupportedCriteriaField
                  selectionCriteria={selectionCriteria}
                  value={supportedCriterionKeys}
                  onChange={(next) => setValue("supportedCriterionKeys", next, { shouldValidate: true })}
                  error={errors.supportedCriterionKeys?.message}
                />
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button type="submit" colorScheme="teal" isLoading={isLoading}>
              {t("Save")}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
